import { authService } from './auth';
import { getApiConfig } from './api-config';

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
const DEFAULT_TIMEOUT = 15000;

const requestCache = new Map<string, { data: any, timestamp: number }>();
const CACHE_TTL = 60000;

export const httpClient = {
  async fetch(url: string, options: RequestInit = {}, _isRetry = false): Promise<Response> {
    const token = authService.getToken();

    const headers = new Headers(options.headers || {});

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const storedUser = localStorage.getItem('flux_user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        if (user && user.companyId) {
          headers.set('x-company-id', user.companyId);
        }
      } catch (error) {
        console.error('Error parsing stored user for x-company-id header:', error);
      }
    }

    const requestOptions: RequestInit = { ...options, headers };

    const config = getApiConfig();

    if (config.debug) {
      console.log(`[HTTP Request] ${options.method || 'GET'} ${url}`, {
        headers: Object.fromEntries(headers.entries()),
        ...requestOptions
      });
    }

    if (config.latencyMs > 0) {
      await sleep(config.latencyMs);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT);

    if (options.signal) {
      options.signal.addEventListener('abort', () => controller.abort());
    }

    try {
      const response = await fetch(url, { ...requestOptions, signal: controller.signal });

      if (config.debug) {
        console.log(`[HTTP Response] ${response.status} ${url}`, {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries())
        });
      }

      if (response.status === 401 && !_isRetry && token) {
        const refreshed = await authService.refreshAccessToken();
        if (refreshed) {
          return this.fetch(url, options, true);
        }
        const sessionError = new Error('Sesión expirada. Por favor inicia sesión nuevamente.');
        (sessionError as any).status = 401;
        (sessionError as any).sessionExpired = true;
        throw sessionError;
      }

      if (response.status === 403) {
        const errorText = await response.text().catch(() => 'Unknown error');
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText };
        }

        // Si el 403 es por email no verificado, redirigir a verificación
        if (errorData.message === 'Debes verificar tu email antes de continuar') {
          authService.fireEmailNotVerified();
        }

        const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
        (error as any).status = response.status;
        (error as any).response = response;
        (error as any).data = errorData;
        throw error;
      }

      if (response.status >= 400) {
        const errorText = await response.text().catch(() => 'Unknown error');
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText };
        }

        const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
        (error as any).status = response.status;
        (error as any).response = response;
        (error as any).data = errorData;

        throw error;
      }

      return response;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        const timeoutError = new Error(`Request timeout: ${url}`);
        (timeoutError as any).status = 0;
        (timeoutError as any).timeout = true;
        throw timeoutError;
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  },

  async get<T>(url: string, options: Omit<RequestInit, 'method'> & { shouldCache?: boolean } = {}): Promise<T> {
    const { shouldCache = false, ...fetchOptions } = options;

    if (shouldCache) {
      const cacheKey = url + JSON.stringify(fetchOptions);
      const cachedResponse = requestCache.get(cacheKey);
      const now = Date.now();

      if (cachedResponse && now - cachedResponse.timestamp < CACHE_TTL) {
        const config = getApiConfig();
        if (config.debug) {
          console.log(`[HTTP Cache Hit] GET ${url}`, cachedResponse.data);
        }
        return cachedResponse.data;
      }
    }

    const response = await this.fetch(url, { ...fetchOptions, method: 'GET' });
    const data = await response.json();

    const config = getApiConfig();
    if (config.debug) {
      console.log(`[HTTP Data] ${url}`, data);
    }

    if (shouldCache) {
      const cacheKey = url + JSON.stringify(fetchOptions);
      requestCache.set(cacheKey, { data, timestamp: Date.now() });
    }

    return data;
  },

  async post<T>(url: string, data: any, options: Omit<RequestInit, 'method' | 'body'> = {}): Promise<Response> {
    return this.fetch(url, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        ...options.headers,
        'Content-Type': 'application/json'
      }
    });
  },

  async put<T>(url: string, data: any, options: Omit<RequestInit, 'method' | 'body'> = {}): Promise<Response> {
    return this.fetch(url, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
      headers: {
        ...options.headers,
        'Content-Type': 'application/json'
      }
    });
  },

  async patch<T>(url: string, data: any, options: Omit<RequestInit, 'method' | 'body'> = {}): Promise<T> {
    const response = await this.fetch(url, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: {
        ...options.headers,
        'Content-Type': 'application/json'
      }
    });
    return response.json();
  },

  async delete<T>(url: string, options: Omit<RequestInit, 'method'> = {}): Promise<Response> {
    return this.fetch(url, {
      ...options,
      method: 'DELETE'
    });
  },

  clearCache() {
    requestCache.clear();
  },

  invalidateCache(url: string) {
    Array.from(requestCache.keys()).forEach((key) => {
      if (key.startsWith(url)) {
        requestCache.delete(key);
      }
    });
  }
};
